import _regeneratorRuntime from "@babel/runtime/helpers/regeneratorRuntime";
import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _objectSpread from "@babel/runtime/helpers/objectSpread2";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import { createContext, useState } from "react";
import { useAmplitude } from "@crehana/analytics";
import { useMe } from "@crehana/auth";
import { useTranslation } from "@crehana/i18n";
import { Snackbar } from "@crehana/ui";
import { AMPLITUDE_ACTION_EVENT_NAMES, SAVED_COURSES_TRANSLATION_KEY } from "./constants";
import useDeleteCourse from "./hooks/useDeleteCourse";
import useSavedCourse from "./hooks/useSavedCourse";
import LOCALES from "./i18n";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
export var SavedCoursesContext = /*#__PURE__*/createContext(undefined);
var SavedCoursesProvider = function SavedCoursesProvider(_ref) {
  var _ref$isDark = _ref.isDark,
    isDark = _ref$isDark === void 0 ? false : _ref$isDark,
    children = _ref.children;
  var _useAmplitude = useAmplitude(),
    logEvent = _useAmplitude.logEvent;
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    loading = _useState2[0],
    setLoading = _useState2[1];
  var _useState3 = useState({
      open: false,
      message: '',
      type: 'success'
    }),
    _useState4 = _slicedToArray(_useState3, 2),
    snackbarData = _useState4[0],
    setSnackbarData = _useState4[1];
  var _useTranslation = useTranslation(SAVED_COURSES_TRANSLATION_KEY),
    t = _useTranslation.t,
    addResourceBundle = _useTranslation.addResourceBundle;
  var _useSavedCourse = useSavedCourse(),
    saveCourseHook = _useSavedCourse.saveCourse;
  var _useDeleteCourse = useDeleteCourse(),
    deleteCourseHook = _useDeleteCourse.deleteCourse;
  addResourceBundle(LOCALES);
  var _useMe = useMe(),
    me = _useMe.me;

  /** @private */
  var getCommonAmplitudeData = function getCommonAmplitudeData(args) {
    return {
      'Course ID': args.courseId,
      'Course Name': args.courseName,
      Category: args.category,
      Subcategory: args.subcategory,
      Source: args.source,
      'Source Detail': args.sourceDetail
    };
  };

  /** @private */
  var openSnackbar = function openSnackbar(msg) {
    var type = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 'success';
    setSnackbarData({
      message: msg,
      open: true,
      type: type
    });
  };

  /** @private */
  var handleErrorResponse = function handleErrorResponse(reason, actionType) {
    openSnackbar(actionType === 'save' ? t('SC_ERROR_SAVE_COURSE') : t('SC_ERROR_DELETE_COURSE'), 'error');
    throw new Error(reason);
  };

  /** @private */
  var closeSnackbar = function closeSnackbar() {
    return setSnackbarData(function (prev) {
      return _objectSpread(_objectSpread({}, prev), {}, {
        open: false
      });
    });
  };

  /** @public */
  var isEnabledSaveCourses = !!(me !== null && me !== void 0 && me.isPremium);

  /** @public */
  var saveCourse = /*#__PURE__*/function () {
    var _ref2 = _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime().mark(function _callee(courseId, args) {
      var _args, promiseResult, response;
      return _regeneratorRuntime().wrap(function _callee$(_context) {
        while (1) switch (_context.prev = _context.next) {
          case 0:
            _args = _objectSpread({
              launchAmplitudeEvent: true,
              showSnackbar: true
            }, args);
            setLoading(true);
            promiseResult = saveCourseHook(courseId);
            _context.prev = 3;
            _context.next = 6;
            return promiseResult;
          case 6:
            response = _context.sent;
            if (_args.showSnackbar) {
              openSnackbar(t('SC_BOOKMARK_ADDED_COURSE'));
            }
            if (_args.launchAmplitudeEvent) {
              logEvent({
                event: AMPLITUDE_ACTION_EVENT_NAMES.COURSE_SAVED,
                data: getCommonAmplitudeData(_objectSpread({
                  courseId: courseId
                }, _args.amplitudeData))
              });
            }
            return _context.abrupt("return", response);
          case 12:
            _context.prev = 12;
            _context.t0 = _context["catch"](3);
            return _context.abrupt("return", handleErrorResponse(_context.t0, 'save'));
          case 15:
            _context.prev = 15;
            setLoading(false);
            return _context.finish(15);
          case 18:
          case "end":
            return _context.stop();
        }
      }, _callee, null, [[3, 12, 15, 18]]);
    }));
    return function saveCourse(_x, _x2) {
      return _ref2.apply(this, arguments);
    };
  }();

  /** @public */
  var deleteCourse = /*#__PURE__*/function () {
    var _ref3 = _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime().mark(function _callee2(courseId, args) {
      var _args, promiseResult, response;
      return _regeneratorRuntime().wrap(function _callee2$(_context2) {
        while (1) switch (_context2.prev = _context2.next) {
          case 0:
            _args = _objectSpread({
              launchAmplitudeEvent: true,
              showSnackbar: true
            }, args);
            setLoading(true);
            promiseResult = deleteCourseHook(courseId);
            _context2.prev = 3;
            _context2.next = 6;
            return promiseResult;
          case 6:
            response = _context2.sent;
            if (_args.showSnackbar) {
              openSnackbar(t('SC_BOOKMARK_REMOVED_COURSE'));
            }
            if (_args.launchAmplitudeEvent) {
              logEvent({
                event: AMPLITUDE_ACTION_EVENT_NAMES.REMOVE_COURSE,
                data: getCommonAmplitudeData(_objectSpread({
                  courseId: courseId
                }, _args.amplitudeData))
              });
            }
            return _context2.abrupt("return", response);
          case 12:
            _context2.prev = 12;
            _context2.t0 = _context2["catch"](3);
            return _context2.abrupt("return", handleErrorResponse(_context2.t0, 'delete'));
          case 15:
            _context2.prev = 15;
            setLoading(false);
            return _context2.finish(15);
          case 18:
          case "end":
            return _context2.stop();
        }
      }, _callee2, null, [[3, 12, 15, 18]]);
    }));
    return function deleteCourse(_x3, _x4) {
      return _ref3.apply(this, arguments);
    };
  }();

  /** @public */
  var toggleBookmarkSelection = function toggleBookmarkSelection(isSavedCourse, courseId, args) {
    return isSavedCourse ? deleteCourse(courseId, args) : saveCourse(courseId, args);
  };
  var providerValue = {
    saveCourse: saveCourse,
    deleteCourse: deleteCourse,
    toggleBookmarkSelection: toggleBookmarkSelection,
    isEnabledSaveCourses: isEnabledSaveCourses,
    loading: loading
  };
  var snackbar = process.env.NODE_ENV === 'test' ? undefined : /*#__PURE__*/_jsx(Snackbar, {
    open: snackbarData.open,
    position: "top-right",
    state: snackbarData.type === 'error' ? 'alert' : 'success',
    dismissOnDelay: 3000,
    onDismiss: closeSnackbar,
    isDark: isDark,
    message: snackbarData.message,
    dismissOnClickOutside: true
  });
  return /*#__PURE__*/_jsxs(SavedCoursesContext.Provider, {
    value: providerValue,
    children: [children, snackbar]
  });
};
export default SavedCoursesProvider;